import { SourcesIcon } from "#components/ui/SourcesIcon.tsx";
import { inlineSourceIcons } from "#features/conversations/types/channelGlyphs.tsx";
import { channels, getConversationSourceChannel, type ConnectorChannel } from "#features/conversations/types/channels.tsx";
import { useSharedTranslation } from "#hooks/useSharedTranslation.ts";
import type { IconAssets } from "#types/assets.ts";
import type { ConversationSource, DataSource, JiraIssueCustomProperties, RemoteMcpItemCustomProperties, RemoteMcpItemKind } from "#types/types.ts";
import { useMemo, type ReactNode } from "react";

export interface CitationSourceApp {
    channel: ConnectorChannel;
    /** False for a source with no known app: it names no app and keeps the neutral mark. */
    isKnownApp: boolean;
    /** The monochrome `currentColor` mark a pill shows at rest. */
    glyph: ReactNode;
    /** The app's mark in its own colours (the CDN `-alt` icon), or null when the mark stays neutral. */
    brandMark: IconAssets | null;
    /** The brand mark on the dark scheme: its light variant for a near-black mark, else `brandMark`. */
    brandMarkDark: IconAssets | null;
}

const NEUTRAL_GLYPH = <SourcesIcon />;

/** The app a source comes from, and the marks a citation pill and the sources chip draw for it. */
export const getCitationSourceApp = (source: ConversationSource): CitationSourceApp => {
    const channel = getConversationSourceChannel(source);
    const isKnownApp = channel !== channels.None;
    // A remote-MCP item with no vendor key has no app colours: its fallback logo is Workleap AI's.
    const hasBrand = isKnownApp && channel !== channels.UserRemoteMcp;

    return {
        channel,
        isKnownApp,
        glyph: (isKnownApp && inlineSourceIcons[channel.nameKey]) || NEUTRAL_GLYPH,
        brandMark: hasBrand ? channel.iconUrlAlt : null,
        brandMarkDark: hasBrand ? channel.iconUrlAltDark ?? channel.iconUrlAlt : null
    };
};

// What the item is in its app ("Meeting", "Message"). Typed on DataSource so a new source type
// fails to compile until it has a label.
const SOURCE_TYPE_LABEL_KEYS: Record<DataSource, string> = {
    unknown: "sourceTypes.unknown",
    confluence_page: "sourceTypes.page",
    jira_issue: "sourceTypes.issue",
    notion_page: "sourceTypes.page",
    outlook_calendar_event: "sourceTypes.meeting",
    outlook_message: "sourceTypes.email",
    sharepoint_file: "sourceTypes.file",
    sharepoint_page: "sourceTypes.page",
    slack_message: "sourceTypes.message",
    teams_message: "sourceTypes.message",
    web_page: "sourceTypes.webPage",
    workleap_files_library_file: "sourceTypes.file",
    workleap_officevibe_engagement_score: "sourceTypes.engagementScore",
    workleap_officevibe_feedback: "sourceTypes.feedback",
    workleap_officevibe_oneonone_agenda: "sourceTypes.oneOnOneAgenda",
    workleap_officevibe_recognition: "sourceTypes.goodVibe",
    workleap_lms_course: "sourceTypes.course",
    workleap_lms_enrollment: "sourceTypes.enrollment",
    workleap_performance_management_goal: "sourceTypes.goal",
    workleap_performance_management_plan: "sourceTypes.plan",
    workleap_performance_management_peer_review_nomination: "sourceTypes.peerReviewNomination",
    workleap_performance_management_review: "sourceTypes.review",
    workleap_performance_management_review_cycle: "sourceTypes.reviewCycle",
    table: "sourceTypes.table",
    google_drive_file: "sourceTypes.file",
    google_calendar_event: "sourceTypes.event",
    gmail_message: "sourceTypes.email",
    remote_mcp_item: "sourceTypes.item",
    workleap_app_meeting: "sourceTypes.meeting",
    teams_meeting_transcript: "sourceTypes.meeting"
};

const REMOTE_MCP_KIND_LABEL_KEYS: Record<RemoteMcpItemKind, string> = {
    unknown: "sourceTypes.item",
    jira_issue: "sourceTypes.issue",
    confluence_page: "sourceTypes.page",
    loom_video: "sourceTypes.video"
};

const getTypeLabelKey = (source: ConversationSource) => {
    if (source.sourceType === "remote_mcp_item") {
        const { kind } = (source.customProperties ?? {}) as RemoteMcpItemCustomProperties;

        return (kind && REMOTE_MCP_KIND_LABEL_KEYS[kind]) || SOURCE_TYPE_LABEL_KEYS.remote_mcp_item;
    }

    return SOURCE_TYPE_LABEL_KEYS[source.sourceType] ?? SOURCE_TYPE_LABEL_KEYS.unknown;
};

export interface CitationSourceLabels {
    /** The app's name, or null for a source with no known app. */
    appName: string | null;
    /** What the item is in its app. */
    typeLabel: string;
}

/** The app name and item type a hover card and a sources row print for a source. */
export const useCitationSourceLabels = (source: ConversationSource): CitationSourceLabels => {
    const { tShared } = useSharedTranslation();

    return useMemo(() => {
        const { channel, isKnownApp } = getCitationSourceApp(source);
        // A Jira issue names its own type ("Epic", "Story"), relayed from the issue.
        const issueType = source.sourceType === "jira_issue"
            ? ((source.customProperties ?? {}) as JiraIssueCustomProperties).issueType
            : null;

        return {
            appName: isKnownApp ? tShared(channel.nameKey) : null,
            typeLabel: issueType || tShared(getTypeLabelKey(source))
        };
    }, [source, tShared]);
};

/** "Type · Date", or the type alone when the source has no date. */
export const joinSourceMeta = (...parts: (string | null | undefined)[]) => parts.filter(Boolean).join(" · ");
